"use client";

import {
	IconAlertTriangle,
	IconArrowLeft,
	IconLoader,
	IconX,
} from "@tabler/icons-react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import * as React from "react";
import { type FieldErrors, FormProvider } from "react-hook-form";
import toast from "react-hot-toast";
import { ProductBasicInfo } from "@/components/products/product-basic-info";
import { ProductImagesUpload } from "@/components/products/product-images-upload";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import type { ProductVariant as ApiProductVariant } from "@/features/products/adapters/create-product.adapter";
import { ProductVariantsSection } from "@/features/products/components/product-variants-section";
import { ProductVideoSection } from "@/features/products/components/product-video-section";
import { VariantEditDrawer } from "@/features/products/components/variant-edit-drawer";
import { VariantImagePickerDrawer } from "@/features/products/components/variant-image-picker-drawer";
import { useCreateProduct } from "@/features/products/hooks/use-create-product";
import { useProductById } from "@/features/products/hooks/use-product-by-id";
import { useProductForm } from "@/features/products/hooks/use-product-form";
import { useUpdateProduct } from "@/features/products/hooks/use-update-product";
import type {
	ProductFormData,
	ProductVariant,
} from "@/features/products/schemas/product-form";
import { transformProductDataToFormData } from "@/features/products/utils/transform-product-data";
import { generateBarcode, generateSKU } from "@/helpers/convert";

export const Route = createFileRoute("/$store/_layout/products_/add")({
	component: AddProductPage,
	validateSearch: (search: Record<string, unknown>) => {
		return {
			edit: (search.edit as string | undefined) || undefined,
		};
	},
});

function normalizeVariantStockValue(value: string): {
	normalized: string | null;
	error: string | null;
} {
	const raw = value.trim();
	if (!raw) return { normalized: null, error: null };

	const parsed = Number(raw);
	if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed < 0) {
		return {
			normalized: null,
			error: "Estoque deve ser um número inteiro maior ou igual a zero",
		};
	}

	return { normalized: String(parsed), error: null };
}

function AddProductPage() {
	const navigate = useNavigate();
	const { store } = Route.useParams();
	const { edit: productId } = Route.useSearch();
	const isEditMode = !!productId;

	const { data: productData, isLoading: isLoadingProduct } = useProductById(
		productId as string | number | undefined,
	);

	const { mutate: createProduct, isPending: isCreating } = useCreateProduct();
	const { mutate: updateProduct, isPending: isUpdating } = useUpdateProduct();
	const isPending = isCreating || isUpdating;

	const initialFormData = React.useMemo(() => {
		if (isEditMode && productData) {
			return transformProductDataToFormData(productData);
		}
		return undefined;
	}, [isEditMode, productData]);

	const productForm = useProductForm({
		initialData: initialFormData,
	});

	const {
		form,
		images,
		variants: productVariants,
		setVariants: setProductVariants,
		addVariant: addProductVariant,
		updateVariant: updateProductVariant,
		removeVariant: removeProductVariant,
		variantOptions,
		setVariantOptions,
		addVariantOption,
		removeVariantOption,
		updateVariantOption,
		addValueToVariant,
		removeValueFromVariant,
		generateVariantsFromOptions,
		video,
		setVideo,
		addVideo,
		removeVideo,
	} = productForm;

	const status = form.watch("status");
	const title = form.watch("title");

	const [imagePickerVariantId, setImagePickerVariantId] = React.useState<
		string | null
	>(null);

	const [editVariantId, setEditVariantId] = React.useState<string | null>(null);
	const [submitErrors, setSubmitErrors] = React.useState<string[]>([]);

	const isInitialized = React.useRef(false);

	React.useEffect(() => {
		if (initialFormData && !isInitialized.current) {
			if (initialFormData.productImages) {
				images.setImages(initialFormData.productImages);
			}
			if (initialFormData.productVariants) {
				setProductVariants(initialFormData.productVariants);
			}
			if (initialFormData.variantOptions) {
				setVariantOptions(initialFormData.variantOptions);
			}
			if (initialFormData.video !== undefined) {
				setVideo(initialFormData.video ?? null);
			}
			isInitialized.current = true;
		}
	}, [
		initialFormData,
		images.setImages,
		setProductVariants,
		setVariantOptions,
		setVideo,
	]);

	// Reset initialization if productId changes
	React.useEffect(() => {
		isInitialized.current = false;
	}, [productId]);

	React.useEffect(() => {
		if (title?.trim() && productVariants.length > 0) {
			setProductVariants((prev) =>
				prev.map((variant, index) => {
					const updates: Partial<ProductVariant> = {};

					if (!variant.sku || variant.sku.trim() === "") {
						updates.sku = generateSKU(title, index);
					}

					if (!variant.barcode || variant.barcode.trim() === "") {
						updates.barcode = generateBarcode(index);
					}

					return Object.keys(updates).length > 0
						? { ...variant, ...updates }
						: variant;
				}),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [title, productVariants.length, setProductVariants]);

	function collectFieldErrors(
		fieldErrors: FieldErrors<ProductFormData>,
	): string[] {
		const messages: string[] = [];
		const fieldLabels: Record<string, string> = {
			title: "Título do Produto",
			description: "Descrição",
			status: "Status",
			stock: "Estoque",
			weight: "Peso",
			height: "Altura",
			width: "Largura",
			length: "Comprimento",
			categoryIds: "Categorias",
			tagIds: "Tags",
		};

		for (const [key, error] of Object.entries(fieldErrors)) {
			if (!error) continue;
			const label = fieldLabels[key] || key;
			if ("message" in error && typeof error.message === "string") {
				messages.push(`${label}: ${error.message}`);
			} else {
				messages.push(`${label} contém valor inválido`);
			}
		}
		return messages;
	}

	function handleInvalid(fieldErrors: FieldErrors<ProductFormData>) {
		const errors = collectFieldErrors(fieldErrors);
		setSubmitErrors(errors);
		toast.error("Corrija os erros antes de salvar");
	}

	function handleSubmit(data: ProductFormData) {
		const errors: string[] = [];

		const parsedStock = data.stock ? Number(data.stock) : undefined;
		const stock = Number.isFinite(parsedStock as number)
			? (parsedStock as number)
			: undefined;

		const parsedWeightGrams = data.weight ? Number(data.weight) : undefined;
		const weightGrams = Number.isFinite(parsedWeightGrams as number)
			? (parsedWeightGrams as number)
			: undefined;

		const parsedHeightCm = data.height ? Number(data.height) : undefined;
		const heightMm =
			Number.isFinite(parsedHeightCm as number) &&
			(parsedHeightCm as number) > 0
				? (parsedHeightCm as number) * 10
				: undefined;

		const parsedWidthCm = data.width ? Number(data.width) : undefined;
		const widthMm =
			Number.isFinite(parsedWidthCm as number) && (parsedWidthCm as number) > 0
				? (parsedWidthCm as number) * 10
				: undefined;

		const parsedLengthCm = data.length ? Number(data.length) : undefined;
		const lengthMm =
			Number.isFinite(parsedLengthCm as number) &&
			(parsedLengthCm as number) > 0
				? (parsedLengthCm as number) * 10
				: undefined;

		const properties = variantOptions
			.filter((option) => option.values.length > 0)
			.map((option) => ({
				title: option.name,
				options: option.values,
			}));

		const imageFiles = images.images
			.filter((img) => img.file !== null)
			.map((img) => img.file as File);

		if (productVariants.length === 0) {
			errors.push(
				"É necessário criar pelo menos uma variação com SKU, Preço e Código de barras",
			);
		}

		const variants: ApiProductVariant[] = [];

		for (const variant of productVariants) {
			const variantLabel = variant.title || "Sem título";
			const priceBrlValue = variant.priceBrl
				? parseFloat(variant.priceBrl.replace(/[^\d.,]/g, "").replace(",", "."))
				: 0;
			const normalizedVariantStock = normalizeVariantStockValue(
				variant.stock || "",
			);

			if (!variant.sku || variant.sku.trim() === "") {
				errors.push(`Variação "${variantLabel}": SKU é obrigatório`);
			}

			if (!priceBrlValue || priceBrlValue <= 0) {
				errors.push(
					`Variação "${variantLabel}": Preço deve ser maior que zero`,
				);
			}

			if (!variant.barcode || variant.barcode.trim() === "") {
				errors.push(
					`Variação "${variantLabel}": Código de barras é obrigatório`,
				);
			}

			if (normalizedVariantStock.error) {
				errors.push(`Variação "${variantLabel}": ${normalizedVariantStock.error}`);
			}

			const imageRef =
				variant.imageId
					? { imageId: variant.imageId }
					: {};

			const nextMetadata = {
				...(variant.metadata || {}),
			} as Record<string, string | undefined>;

			if (normalizedVariantStock.normalized !== null) {
				nextMetadata.stock = normalizedVariantStock.normalized;
			} else {
				delete nextMetadata.stock;
			}

			const metadata =
				Object.keys(nextMetadata).length > 0 ? nextMetadata : undefined;

			const variantData: ApiProductVariant = {
				sku: (variant.sku || "").trim(),
				priceBrl: priceBrlValue,
				title: variant.title || "",
				barcode: (variant.barcode || "").trim(),
				isDefault: variant.isDefault,
				compareAtBrl: variant.compareAtBrl
					? parseFloat(
							variant.compareAtBrl.replace(/[^\d.,]/g, "").replace(",", "."),
						)
					: undefined,
				metadata,
				...imageRef,
			};

			Object.keys(variantData).forEach((key) => {
				if (
					variantData[key as keyof typeof variantData] === undefined &&
					key !== "isDefault"
				) {
					delete variantData[key as keyof typeof variantData];
				}
			});

			variants.push(variantData);
		}

		if (errors.length > 0) {
			setSubmitErrors(errors);
			toast.error("Corrija os erros antes de salvar");
			return;
		}

		setSubmitErrors([]);

		const hasDefault = variants.some((v) => v.isDefault);
		if (!hasDefault && variants.length > 0) {
			variants[0].isDefault = true;
		}

		if (isEditMode && productId) {
			updateProduct(
				{
					id: productId as string | number,
					title: data.title.trim(),
					description: data.description || undefined,
					status: (data.status as "draft" | "active" | "archived") || "draft",
					categoryIds: data.categoryIds?.length ? data.categoryIds : undefined,
					tagIds: data.tagIds?.length ? data.tagIds : undefined,
					variants,
					formImages: images.images,
					images: imageFiles,
					stock,
					weightGrams,
					heightMm,
					widthMm,
					lengthMm,
					properties: properties.length > 0 ? properties : undefined,
					videoFile: video?.file ?? null,
					existingVideoUrl: video ? (video.file ? undefined : video.url) : null,
				},
				{
					onSuccess: () => {
						navigate({
							to: "/$store/products",
							params: { store },
						});
					},
				},
			);
		} else {
			createProduct(
				{
					title: data.title.trim(),
					description: data.description || undefined,
					status: (data.status as "draft" | "active" | "archived") || "draft",
					categoryIds: data.categoryIds?.length ? data.categoryIds : undefined,
					tagIds: data.tagIds?.length ? data.tagIds : undefined,
					variants,
					images: imageFiles,
					formImages: images.images,
					stock,
					weightGrams,
					heightMm,
					widthMm,
					lengthMm,
					properties: properties.length > 0 ? properties : undefined,
					videoFile: video?.file ?? null,
				},
				{
					onSuccess: () => {
						navigate({
							to: "/$store/products",
							params: { store },
						});
					},
				},
			);
		}
	}

	if (isEditMode && (isLoadingProduct || !productData)) {
		return (
			<div className="flex items-center justify-center h-64">
				<IconLoader className="size-6 animate-spin text-muted-foreground" />
			</div>
		);
	}

	return (
		<div className="mx-auto w-full max-w-3xl space-y-8 pb-12">
			{/* Header */}
			<div className="flex items-center gap-4">
				<Button variant="ghost" size="icon" asChild>
					<Link to="/$store/products" params={{ store }}>
						<IconArrowLeft className="size-5" />
						<span className="sr-only">Back to products</span>
					</Link>
				</Button>
				<div className="flex-1">
					<h1 className="text-2xl font-bold tracking-tight">
						{isEditMode ? "Editar Produto" : "Adicionar Novo Produto"}
					</h1>
					<p className="text-muted-foreground text-sm">
						{isEditMode
							? "Edite as informações do produto"
							: "Crie uma nova listagem de produto para sua loja"}
					</p>
				</div>
				{isLoadingProduct ? (
					<Badge variant="outline">Carregando...</Badge>
				) : (
					<Badge
						variant="outline"
						className={
							status === "active"
								? "text-emerald-600 border-emerald-200 bg-emerald-50"
								: status === "archived"
									? "text-gray-600 border-gray-200 bg-gray-50"
									: "text-amber-600 border-amber-200 bg-amber-50"
						}
					>
						{status === "active"
							? "Ativo"
							: status === "archived"
								? "Arquivado"
								: "Rascunho"}
					</Badge>
				)}
			</div>

			<FormProvider {...form}>
				<form
					onSubmit={form.handleSubmit(handleSubmit, handleInvalid)}
					className="space-y-8"
				>
					<ProductBasicInfo />

					<ProductImagesUpload
						images={images.images}
						onAddImages={images.addImages}
						onRemoveImage={images.removeImage}
						onReorderImages={images.reorderImages}
					/>

					<ProductVideoSection
						video={video}
						onAddVideo={addVideo}
						onRemoveVideo={removeVideo}
					/>

					<ProductVariantsSection
						variants={productVariants}
						variantOptions={variantOptions}
						onAddVariant={addProductVariant}
						onUpdateVariant={updateProductVariant}
						onSetVariants={setProductVariants}
						onAddVariantOption={addVariantOption}
						onRemoveVariantOption={removeVariantOption}
						onUpdateVariantOption={updateVariantOption}
						onAddValueToVariant={addValueToVariant}
						onRemoveValueFromVariant={removeValueFromVariant}
						onGenerateVariantsFromOptions={generateVariantsFromOptions}
						onOpenImagePicker={setImagePickerVariantId}
						onOpenEditDrawer={setEditVariantId}
						onRemoveVariant={removeProductVariant}
						images={images.images}
					/>

					<VariantImagePickerDrawer
						imagePickerVariantId={imagePickerVariantId}
						onClose={() => setImagePickerVariantId(null)}
						images={images.images}
						variants={productVariants}
						onUpdateVariant={updateProductVariant}
					/>

					<VariantEditDrawer
						editVariantId={editVariantId}
						onClose={() => setEditVariantId(null)}
						onNavigate={setEditVariantId}
						variants={productVariants}
						onUpdateVariant={updateProductVariant}
						onRemoveVariant={removeProductVariant}
						isPending={isPending}
					/>

					{/* Inventory & Dimensions */}
					<Card>
						<CardHeader>
							<CardTitle className="flex items-center gap-2">
								<span className="flex size-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">
									5
								</span>
								Inventory & Dimensions
							</CardTitle>
							<CardDescription>
								Manage stock levels and product measurements
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-6">
							<div className="grid grid-cols-2 gap-4">
								<div className="space-y-2">
									<Label htmlFor="stock">Quantidade em Estoque</Label>
									<Input
										id="stock"
										type="number"
										placeholder="0"
										{...form.register("stock")}
									/>
								</div>

								<div className="space-y-2">
									<Label htmlFor="status">Status do Produto</Label>
									<Select
										value={status || "draft"}
										onValueChange={(value) =>
											form.setValue(
												"status",
												value as "draft" | "active" | "archived",
											)
										}
									>
										<SelectTrigger id="status" className="w-full">
											<SelectValue placeholder="Selecione o status" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="draft">
												<div className="flex items-center gap-2">
													<span className="size-2 rounded-full bg-yellow-500" />
													Rascunho
												</div>
											</SelectItem>
											<SelectItem value="active">
												<div className="flex items-center gap-2">
													<span className="size-2 rounded-full bg-emerald-500" />
													Ativo
												</div>
											</SelectItem>
											<SelectItem value="archived">
												<div className="flex items-center gap-2">
													<span className="size-2 rounded-full bg-gray-500" />
													Arquivado
												</div>
											</SelectItem>
										</SelectContent>
									</Select>
								</div>
							</div>

							<Separator />

							<div className="space-y-4">
								<div className="flex items-center gap-2">
									<div className="flex size-5 items-center justify-center rounded bg-purple-100 dark:bg-purple-900/30">
										<span className="text-purple-600 dark:text-purple-400 text-xs font-semibold">
											⚖
										</span>
									</div>
									<Label className="text-sm font-semibold">
										Dimensões e Peso
									</Label>
								</div>

								<div className="space-y-4">
									<div className="space-y-2">
										<Label htmlFor="weight" className="text-xs">
											Peso (gramas)
										</Label>
										<div className="relative">
											<Input
												id="weight"
												type="number"
												step="1"
												placeholder="0"
												{...form.register("weight")}
												className="pr-10"
											/>
											<span className="text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2 text-xs">
												g
											</span>
										</div>
										<p className="text-muted-foreground text-xs">
											Usado para cálculos de frete
										</p>
									</div>

									<div className="grid grid-cols-3 gap-3">
										<div className="space-y-2">
											<Label htmlFor="height" className="text-xs">
												Altura (cm)
											</Label>
											<div className="relative">
												<Input
													id="height"
													type="number"
													step="0.1"
													placeholder="0"
													{...form.register("height")}
													className="pr-8"
												/>
												<span className="text-muted-foreground absolute right-2 top-1/2 -translate-y-1/2 text-xs">
													cm
												</span>
											</div>
										</div>

										<div className="space-y-2">
											<Label htmlFor="width" className="text-xs">
												Largura (cm)
											</Label>
											<div className="relative">
												<Input
													id="width"
													type="number"
													step="0.1"
													placeholder="0"
													{...form.register("width")}
													className="pr-8"
												/>
												<span className="text-muted-foreground absolute right-2 top-1/2 -translate-y-1/2 text-xs">
													cm
												</span>
											</div>
										</div>

										<div className="space-y-2">
											<Label htmlFor="length" className="text-xs">
												Comprimento (cm)
											</Label>
											<div className="relative">
												<Input
													id="length"
													type="number"
													step="0.1"
													placeholder="0"
													{...form.register("length")}
													className="pr-8"
												/>
												<span className="text-muted-foreground absolute right-2 top-1/2 -translate-y-1/2 text-xs">
													cm
												</span>
											</div>
										</div>
									</div>
									<p className="text-muted-foreground text-xs">
										Dimensões do produto para embalagem e envio
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					{submitErrors.length > 0 && (
						<div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4">
							<div className="flex items-start gap-3">
								<IconAlertTriangle className="mt-0.5 size-5 shrink-0 text-destructive" />
								<div className="flex-1 space-y-2">
									<p className="text-sm font-semibold text-destructive">
										{submitErrors.length === 1
											? "1 erro encontrado"
											: `${submitErrors.length} erros encontrados`}
									</p>
									<ul className="space-y-1">
										{submitErrors.map((error) => (
											<li
												key={error}
												className="text-sm text-destructive/90"
											>
												&bull; {error}
											</li>
										))}
									</ul>
								</div>
								<button
									type="button"
									onClick={() => setSubmitErrors([])}
									className="shrink-0 rounded-md p-1 text-destructive/70 hover:bg-destructive/10 hover:text-destructive transition-colors"
								>
									<IconX className="size-4" />
								</button>
							</div>
						</div>
					)}

					{/* Submit Actions */}
					<div className="sticky bottom-0 -mx-4 flex items-center justify-end gap-3 border-t bg-background/95 px-4 py-4 backdrop-blur supports-backdrop-filter:bg-background/60">
						<Button type="button" variant="outline" asChild>
							<Link to="/$store/products" params={{ store }}>
								Cancelar
							</Link>
						</Button>
						<Button
							type="submit"
							className="min-w-[140px]"
							disabled={isPending || isLoadingProduct}
						>
							{isPending
								? "Salvando..."
								: isEditMode
									? "Atualizar Produto"
									: "Salvar Produto"}
						</Button>
					</div>
				</form>
			</FormProvider>
		</div>
	);
}
